import {
  Button,
  Card,
  Col,
  Divider,
  EditButton,
  Icons,
  ListButton,
  Popconfirm,
  Row,
  Show,
  Table,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useNavigation, useOne } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { IDamageGood } from 'interfaces';
import { deleteDamage } from 'services/damage';
import styles from './style.module.css';

const { Title, Text } = Typography;

export const DamageShow = () => {
  const { list } = useNavigation();
  const { id } = useParams();
  const { data, isLoading } = useOne<IDamageGood>({
    resource: 'damageCopy',
    id: id || '',
    metaData: {
      fields: [
        'id',
        'damageDetails',
        'totalAmount',
        {
          user: ['name'],
        },
        {
          warehouse: ['name'],
        },
        {
          damageProductsCopy: [
            'id',
            'quantity',
            'lossAmount',
            {
              productVariant: [
                'id',
                'name',
                'sku',
                {
                  productCopy: ['name'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  const { data: canAccessDelete } = useCan({
    resource: 'damages',
    action: 'delete',
    params: { field: 'id' },
  });

  const record = data?.data;
  const damageProducts = record?.damageProductsCopy.map((item) => {
    return {
      id: item.id,
      sku: item.productVariant.sku,
      variantName: item.productVariant.name,
      productName: item.productVariant.productCopy.name,
      quantity: item.quantity,
      lossAmount: Number.parseFloat(item.lossAmount.toFixed(2)),
    };
  });

  const handleDelete = async (id?: string) => {
    if (!id) return;

    try {
      await deleteDamage(id);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a damage.',
      });
      list('damages');
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete damage.',
      });
    }
  };

  const pageHeader = (
    <>
      <ListButton />
      <EditButton hidden />
      <Popconfirm
        title="Are you sure?"
        okText="Delete"
        onConfirm={() => handleDelete(id)}
        disabled={!canAccessDelete?.can}
      >
        <Button
          size="middle"
          danger
          icon={<Icons.DeleteOutlined />}
          disabled={!canAccessDelete?.can}
        >
          Delete
        </Button>
      </Popconfirm>
    </>
  );

  const footer = () => {
    const subTotal = damageProducts?.reduce((total, product) => {
      total += Number.parseFloat(product.lossAmount.toFixed(2));
      return total;
    }, 0);

    return (
      <div className={styles['product-footer']}>
        <Text>Sub-Total: {subTotal}</Text>
      </div>
    );
  };

  return (
    <Show isLoading={isLoading} headerProps={{ extra: pageHeader }}>
      <Row justify="space-around" align="middle">
        <Col xs={24} lg={18}>
          <Card>
            <Title level={5}>Reported By</Title>
            <Text>{record?.user?.name ?? 'N/A'}</Text>
            <Title level={5}>Warehouse</Title>
            <Text>{record?.warehouse.name}</Text>
            <Title level={5}>Note</Title>
            <Text>{record?.damageDetails}</Text>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row justify="space-around" align="middle">
        <Col xs={24} lg={18}>
          <Table
            dataSource={damageProducts}
            rowKey="id"
            scroll={{ x: true }}
            footer={(_data) => footer()}
          >
            <Table.Column dataIndex="sku" title="SKU" />
            <Table.Column dataIndex="variantName" title="Variant Name" />
            <Table.Column dataIndex="productName" title="Product Name" />
            <Table.Column dataIndex="quantity" title="Quantity" />
            <Table.Column dataIndex="lossAmount" title="Loss Amount" />
          </Table>
        </Col>
      </Row>
    </Show>
  );
};
